import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormField, TableSection } from '@shared/components/add-form/add-form.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PurchaseReturnOrderItem {
  id: number;
  porNo: string; // POR Number (alias for rtvNo from API)
  rtvNo?: string; // Keep for backward compatibility with API
  poCode: string; // PO Code
  poId?: number | null; // PO ID (optional)
  vendorId?: number; // Vendor ID from API (for resolving vendorCode/vendorName)
  vendorCode: string;
  vendorName: string;
  warehouseCode: string;
  warehouseName: string;
  returnQty: number; // Total return quantity
  returnAmount: number; // Return amount
  reason: string;
  status: string;
  creationDate: string;
  returnDate?: string;
}

export interface PurchaseReturnOrderListResponse {
  data: PurchaseReturnOrderItem[];
  totalRecords: number;
  loading: boolean;
}

export interface AvailablePO {
  id: number;
  poNo: string;
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  status: string;
  creationDate: string;
  deliveryDate: string;
  grnCount: number; // Số lượng GRN hợp lệ
}

export interface SKUFromGRN {
  vendorSkuName?: string;
  vendorSkuCode?: string;
  grnDetailId: number;
  skuId?: number; // Optional - may not be available for manually added items
  skuCode: string;
  skuName: string;
  spuCode: string;
  spuName: string;
  qtyReceived: number;
  qtyReturnedBefore: number;
  qtyAvailable: number; // qtyReceived - qtyReturnedBefore
  currentStock: number; // Tồn kho hiện tại
  price: number;
  returnQty?: number; // User input, default 0
  reason?: string; // Optional reason per item
  tax?: number; // Tax amount
  taxAmount?: number; // Tax amount (percentage or rate)
  id?: string; // Optional ID for table rows
}

export interface PurchaseReturnOrderAddFormData {
  vendorId: number;
  vendorCode: string;
  vendorName: string;
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  poId: number | null;
  poNo: string | null;
  reason: string;
  code?: string; // POR code/number - optional, will be used if provided
  items: SKUFromGRN[];
}

export interface PurchaseReturnOrderDetailItem {
  id: string;
  grnDetailId: number;
  skuCode: string;
  skuName: string;
  vendorSkuCode?: string;
  vendorSkuName?: string;
  spuCode: string;
  spuName: string;
  uom: string;
  qtyReceived: number;
  qtyReturnedBefore: number;
  qtyAvailable: number;
  currentStock: number;
  returnQty: number;
  price: number;
  returnAmount: number;
  taxAmount: number; // Percentage for display (8)
  tax?: number; // Rate from API (0.08) - used when saving
  reason?: string;
}

// ============================================================================
// STATUS OPTIONS
// ============================================================================

export const POR_STATUS_OPTIONS = [
  { label: 'purchase_return_order.status.pending', value: 'PENDING' },
  { label: 'purchase_return_order.status.approved', value: 'APPROVED' },
  { label: 'purchase_return_order.status.in_progress', value: 'IN_PROGRESS' },
  { label: 'purchase_return_order.status.partially_shipped', value: 'PARTIALLY_SHIPPED' },
  { label: 'purchase_return_order.status.fully_shipped', value: 'FULLY_SHIPPED' },
  { label: 'purchase_return_order.status.received_by_vendor', value: 'RECEIVED_BY_VENDOR' },
  { label: 'purchase_return_order.status.completed', value: 'COMPLETED' },
  { label: 'purchase_return_order.status.cancelled', value: 'CANCELLED' },
];

// ============================================================================
// SEARCH FORM CONFIGURATION
// ============================================================================

export const POR_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'poNo',
    label: 'purchase_return_order.search.fields.po_number',
    type: 'text',
    placeholder: 'purchase_return_order.search.placeholders.enter_po_number',
  },
  {
    key: 'porNo',
    label: 'purchase_return_order.search.fields.por_number',
    type: 'text',
    placeholder: 'purchase_return_order.search.placeholders.enter_por_number',
  },
  {
    key: 'vendorCode',
    label: 'purchase_return_order.search.fields.vendor',
    type: 'select',
    placeholder: 'purchase_return_order.search.placeholders.enter_vendor',
    options: [], // Will be populated dynamically
    showClear: true,
  },
  {
    key: 'warehouseCode',
    label: 'purchase_return_order.search.fields.warehouse',
    type: 'select',
    placeholder: 'purchase_return_order.search.placeholders.enter_warehouse',
    options: [], // Will be populated dynamically
    showClear: true,
  },
  {
    key: 'status',
    label: 'purchase_return_order.search.fields.status',
    type: 'select',
    options: POR_STATUS_OPTIONS,
    showClear: true,
  },
  {
    key: 'dateRange',
    label: 'purchase_return_order.search.fields.date_range',
    type: 'dateRange',
    range: { startKey: 'beginDate', endKey: 'endDate' },
  },
];

// ============================================================================
// TABLE COLUMNS
// ============================================================================

/**
 * Default column property names for Purchase Return Order table
 */
export const POR_DEFAULT_COLUMNS = {
  POR_NO: 'porNo',
  PO_CODE: 'poCode',
  VENDOR_NAME: 'vendorName',
  WAREHOUSE_NAME: 'warehouseName',
  REASON: 'reason',
  STATUS: 'status',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Purchase Return Order table
 */
export const POR_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Purchase Return Order table
 */
export const POR_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Purchase Return Order table
 */
export const POR_DEFAULT_COLUMNS_ORDER: string[] = [
  POR_DEFAULT_COLUMNS.POR_NO,
  POR_DEFAULT_COLUMNS.PO_CODE,
  POR_DEFAULT_COLUMNS.VENDOR_NAME,
  POR_DEFAULT_COLUMNS.WAREHOUSE_NAME,
  POR_DEFAULT_COLUMNS.REASON,
  POR_DEFAULT_COLUMNS.STATUS,
  POR_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Purchase Return Order table
 */
export const POR_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const POR_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'purchase_return_order.table.columns.por_no',
    prop: 'porNo',
    width: 160,
    type: 'text',
  },
  {
    label: 'purchase_return_order.table.columns.po_code',
    prop: 'poCode',
    width: 160,
    type: 'text',
  },
  {
    label: 'purchase_return_order.table.columns.vendor',
    prop: 'vendorName',
    width: 180,
    type: 'text',
  },
  {
    label: 'purchase_return_order.table.columns.warehouse',
    prop: 'warehouseName',
    width: 180,
    type: 'text',
  },
  {
    label: 'purchase_return_order.table.columns.reason',
    prop: 'reason',
    width: 200,
    type: 'text',
  },
  {
    label: 'purchase_return_order.table.columns.status',
    prop: 'status',
    width: 140,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'purchase_return_order.table.columns.created_date',
    prop: 'creationDate',
    width: 160,
    type: 'custom',
    align: 'center',
    sortable: true,
    sortField: 'creationDate',
  },
  {
    label: 'purchase_return_order.table.columns.action',
    prop: 'actions',
    width: 100,
    type: 'actions',
    align: 'center',
  },
];

// ============================================================================
// ADD FORM CONFIGURATION
// ============================================================================

export const POR_ADD_FORM_FIELDS: FormField[] = [
  {
    controlName: 'poId',
    label: 'purchase_return_order.add.fields.po_number',
    type: 'searchableDropdown',
    section: 'purchase_return_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_return_order.add.placeholders.select_po',
    options: [], // Will be populated dynamically
    showClear: false,
  },
  {
    controlName: 'porNo',
    label: 'purchase_return_order.add.fields.por_number',
    type: 'text',
    section: 'purchase_return_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_return_order.add.placeholders.enter_por_number',
    maxLength: 255,
  },
  {
    controlName: 'vendorId',
    label: 'purchase_return_order.add.fields.vendor',
    type: 'searchableDropdown', // Changed from text to searchableDropdown
    section: 'purchase_return_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_return_order.add.placeholders.select_vendor',
    options: [], // Will be populated dynamically
    disabled: false, // Enabled initially - will be disabled when PO is selected
  },
  {
    controlName: 'warehouseId',
    label: 'purchase_return_order.add.fields.warehouse',
    type: 'searchableDropdown',
    section: 'purchase_return_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_return_order.add.placeholders.select_warehouse',
    options: [], // Will be populated dynamically
    disabled: false, // Enabled initially - will be disabled when PO is selected
  },
  {
    controlName: 'reason',
    label: 'purchase_return_order.add.fields.reason',
    type: 'text',
    section: 'purchase_return_order.add.sections.basic_information',
    required: true,
    placeholder: 'purchase_return_order.add.placeholders.enter_reason',
    maxLength: 255,
  },
];

// ============================================================================
// DETAIL TABLE SECTION
// ============================================================================

export const POR_DETAIL_TABLE_SECTION: TableSection = {
  name: 'item_details',
  title: 'purchase_return_order.add.sections.item_details',
  show: true,
  hasCheckbox: true,
  actions: [
    {
      label: 'purchase_return_order.add.actions.delete',
      action: 'deleteSelected',
      severity: 'danger',
      outlined: true,
      icon: 'delete',
    },
    {
      label: 'purchase_return_order.add.actions.add_item',
      action: 'addItem',
      severity: 'info',
      outlined: false,
      icon: 'add',
    }
  ],
  columns: [
    {
      label: 'purchase_return_order.add.table.columns.sku_code',
      prop: 'skuCode',
      width: 140,
      type: 'text',
    },
    {
      label: 'purchase_return_order.add.table.columns.sku_name',
      prop: 'skuName',
      width: 200,
      type: 'text',
    },
    {
      label: 'purchase_return_order.add.table.columns.vendor_sku_code',
      prop: 'vendorSkuCode',
      width: 140,
      type: 'text',
    },
    {
      label: 'purchase_return_order.add.table.columns.vendor_sku_name',
      prop: 'vendorSkuName',
      width: 140,
      type: 'text',
    },
    {
      label: 'purchase_return_order.add.table.columns.qty_purchased',
      prop: 'qtyReceived',
      width: 120,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'purchase_return_order.add.table.columns.unit_price',
      prop: 'price',
      width: 120,
      type: 'currency',
      align: 'right',
    },
    {
      label: 'purchase_return_order.add.table.columns.tax_amount',
      prop: 'taxAmount',
      width: 120,
      type: 'currency',
      align: 'right',
    },
    {
      label: 'purchase_return_order.add.table.columns.return_qty',
      prop: 'returnQty',
      width: 120,
      type: 'inputNumber',
      align: 'right',
      useGrouping: true,
      min: 0,
      required: false,
    },
    {
      label: 'purchase_return_order.add.table.columns.reason',
      prop: 'reason',
      width: 200,
      type: 'inputText',
      required: false,
    },
  ],
  data: [],
};

// ============================================================================
// STATUS HELPERS
// ============================================================================

export function getPORStatusSeverity(status: string): string {
  // Normalize status to uppercase for consistent mapping
  const normalizedStatus = (status || '').toUpperCase().trim();
  
  const statusMap: { [key: string]: string } = {
    'PENDING': 'warning',
    'APPROVED': 'success',
    'REJECTED': 'danger',
    'IN_PROGRESS': 'info',
    'PARTIALLY_SHIPPED': 'warning',
    'FULLY_SHIPPED': 'warning',
    'RECEIVED_BY_VENDOR': 'info',
    'COMPLETED': 'success',
    'CANCELLED': 'danger',
  };
  return statusMap[normalizedStatus] || 'info';
}

export function getPORStatusLabel(
  status: string,
  translateService?: any
): string {
  if (!status) {
    return '-';
  }
  
  // Normalize status to uppercase for consistent mapping
  const normalizedStatus = (status || '').toUpperCase().trim();
  
  const statusMap: { [key: string]: string } = {
    PENDING: 'purchase_return_order.status.pending',
    APPROVED: 'purchase_return_order.status.approved',
    REJECTED: 'purchase_return_order.status.rejected',
    IN_PROGRESS: 'purchase_return_order.status.in_progress',
    PARTIALLY_SHIPPED: 'purchase_return_order.status.partially_shipped',
    FULLY_SHIPPED: 'purchase_return_order.status.fully_shipped',
    RECEIVED_BY_VENDOR: 'purchase_return_order.status.received_by_vendor',
    COMPLETED: 'purchase_return_order.status.completed',
    CANCELLED: 'purchase_return_order.status.cancelled',
  };

  const translationKey = statusMap[normalizedStatus] || 'purchase_return_order.status.pending';
  
  if (translateService) {
    const translated = translateService.instant(translationKey);
    // If translation returns the key itself, it means translation failed
    if (translated === translationKey) {
      console.warn('Translation key not found:', translationKey, 'for status:', normalizedStatus);
    }
    return translated;
  }
  
  return translationKey;
}

export function getPORStatusStyle(status: string): any {
  const severity = getPORStatusSeverity(status);

  switch (severity) {
    case 'success':
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    case 'warning':
      return {
        'background-color': '#fef3c7',
        color: '#a16207',
        border: 'none',
      };
    case 'danger':
      return {
        'background-color': '#fef2f2',
        color: '#dc2626',
        border: 'none',
      };
    case 'info':
    default:
      return {
        'background-color': '#f9fafb',
        color: '#6b7280',
        border: 'none',
      };
  }
}

// ============================================================================
// CURRENCY FORMATTING
// ============================================================================

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

